import { Injectable, signal } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AuthenticationResult, EventMessage, EventPayload, EventType } from '@azure/msal-browser';
import { DataAction, Payload, StateRepository } from '@angular-ru/ngxs/decorators';
import { NgxsDataRepository } from '@angular-ru/ngxs/repositories';
import { Selector, SelectorOptions, State, Store } from '@ngxs/store';
import { produce } from 'immer';
import { combineLatest, Observable, Subject, firstValueFrom } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { Role, RoleNames } from '../../models/role';
import { IUserContext, IUserDto, User } from '../../models/user';
import { RoleService } from './role.service';
import { RoleStateModel, UserStateModel } from './user.state.models';
import { UserEndpoint } from './user-endpoint.service';
import { Mutex } from '../../shared/Mutex';
import { AppInsightsService } from '../app-insights/appinsights.service';

@StateRepository()
@State<UserStateModel>({
    name: 'userState',
    defaults: {

    }
})
@Injectable()
export class UserService extends NgxsDataRepository<UserStateModel> {

    private loadingContext: boolean;
    readonly accessToken = signal<string | null>(null);

    get isAuthenticated(): boolean {
        return this.authService.instance.getAllAccounts().length > 0;
    }

    @Selector()
    static entryPointUser(state: UserStateModel) {
        return state.user;
    }

    @SelectorOptions({ injectContainerState: false })
    @Selector([UserService.entryPointUser])
    static user(user: User) {
        return user ? new User(user) : undefined;
    }

    @Selector()
    static userIdentifier(state: UserStateModel) {
        return state.user ? state.user.userName : undefined;
    }

    @Selector()
    static userRoles(state: UserStateModel) {
        return UserService.instanciateUserRoles(state);
    }

    @Selector([RoleService])
    static currentRole(state: UserStateModel, roleState: RoleStateModel) {
        return UserService.getCurrentRole(state, roleState?.currentRoleName);
    }

    get user$(): Observable<User> {
        return this.store.select<User>(UserService.user);
    }

    get userSync(): User {
        return this.store.selectSnapshot<User>(UserService.user);
    }

    get userRolesSync(): Role[] {
        return UserService.instanciateUserRoles(this.snapshot);
    }

    get currentRole$(): Observable<Role> {
        const roleName$ = this.store.select<string>(RoleService.currentRoleName);
        return combineLatest([this.state$, roleName$]).pipe(filter(([state, roleName]) => !!state && !!roleName), map(([state, roleName]) => UserService.getCurrentRole(state, roleName)));
    }

    get currentRoleSync(): Role {
        return UserService.getCurrentRole(this.snapshot, this.store.selectSnapshot<string>(RoleService.currentRoleName));
    }

    get currentRoleNameSync(): string {
        return this.store.selectSnapshot<string>(RoleService.currentRoleName);
    }

    get userIdentifierSync(): string {
        return this.store.selectSnapshot<string>(UserService.userIdentifier);
    }

    public userContext = new Subject<IUserDto[]>();
    private userList: IUserDto[] = [];
    private roleList: string[] = [];

    constructor(
        private readonly store: Store,
        private readonly authService: MsalService,
        private readonly msalBroadcastService: MsalBroadcastService,
        private readonly endpointService: UserEndpoint,
        private readonly appInsightService: AppInsightsService
    ) {
        super();

        this.userContext.subscribe(userItems => {
            this.userList = [];
            this.userList = [...userItems]
        });
    }

    private static instanciateUserRoles(state: UserStateModel) {
        return state.user?.userContext.roles ? state.user.userContext.roles.map(o => new Role(o)) : undefined;
    }

    private static getCurrentRole(state: UserStateModel, currentRoleName: string) {
        const roleName = currentRoleName;
        const roles = UserService.instanciateUserRoles(state);
        if (roleName && roles) {
            return roles.find(role => role.name === roleName);
        }

        return undefined;
    };

    async ngxsAfterBootstrap() {

        this.initializeLoginListener();
    }

    initializeLoginListener() {

        const loadUserContext = (payload: AuthenticationResult) => {
            if (!this.loadingContext && !this.userSync) {

                setTimeout(async () => {
                    try {
                        this.loadingContext = true;
                        console.log('getting user context');
                        const context: IUserContext = await this.getUserContext(payload.uniqueId);
                        this.setUserInfo(context);
                    } finally {
                        this.loadingContext = false;
                    }
                });

            }
        };


        this.msalBroadcastService.msalSubject$
            .pipe(
                filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS || msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS),
            )
            .subscribe((result: EventMessage) => {
                const activeAccount = this.authService.instance.getActiveAccount();
                const payload = result.payload as AuthenticationResult;

                this.accessToken.set(payload.accessToken);

                if (!activeAccount) {
                    console.log(`login result => `, result);
                    this.setLoginValues(result.payload);
                    this.authService.instance.setActiveAccount(payload.account);
                    loadUserContext(payload);
                }
                else {
                    loadUserContext(payload);
                }


            });
    }

    @DataAction()
    setLoginValues(@Payload('login') payload: EventPayload) {
        this.ctx.setState(produce(this.getState(), (draft: UserStateModel) => {
            draft.loginPayload = payload;
        }));

        return this.userSync;
    }

    async getUserContext(uniqueId: string): Promise<IUserContext> {
        return await firstValueFrom(this.endpointService.getUserContext(uniqueId));
    }

    setUserInfo(userContext: IUserContext) {
        try {
            const user = new User();
            user.userContext = userContext;
            this.setUser(user);
        } catch (error) {
        }
    }

    isInRole(...roles: RoleNames[]): boolean {
        if (this.userSync != null) {
            return this.userRolesSync.some(r => r.inRoles(roles));
        }
        return false;
    }

    logout(): void {
        this.authService.logout();

        this.appInsightService.appInsights?.clearAuthenticatedUserContext();
    }

    /*******************Redux*************************** */
    @DataAction()
    setUser(@Payload('user') payload?: User) {
        this.ctx.setState(produce(this.getState(), (draft: UserStateModel) => {
            draft.user = payload;
        }));

        return this.userSync;
    }

    async getUsersByRoles(roleNames: string[]): Promise<IUserDto[]> {

        let included = (arr: string[], target: string[]) => target.every(v => arr.includes(v));

        if (included(this.roleList, roleNames)) {
            return this.userList;
        }

        this.roleList = [...this.roleList, ...roleNames];
        let userData = await this.endpointService.getUsersByRoles(roleNames);

        if (userData) {
            this.userContext.next(userData);
        }

        return userData ?? [];
    }

    collectionMutex = new Mutex();

    async getUserByUserPrincipleName(email: string): Promise<IUserDto> {

        if (!email) return;

        const unlock = await this.collectionMutex.lock();

        try {
            let result = this.userList.find(n => n.userPrincipalName.toUpperCase() == email?.toUpperCase());

            if (result == null) {
                result = await this.endpointService.getUserByUserPrincipleName(email);
                this.userList.push(result);
            }

            return result;
        } finally {
            unlock();
        }
    }

    async getUserByObjectId(objectId: string): Promise<IUserDto> {

        if (!objectId) return;

        const unlock = await this.collectionMutex.lock();

        try {
            let result = this.userList.find(n => n?.objectId?.toUpperCase() == objectId?.toUpperCase());

            if (result == null) {
                result = await this.endpointService.getUserNameById(objectId);
                this.userList.push(result);
            }

            return result;
        } finally {
            unlock();
        }
    }

    async getUserDisplayName(userUniqueId: string,): Promise<string> {

        let displayName: string = "Unknown";

        if (userUniqueId) {

            let user = await this.getUserByObjectId(userUniqueId);

            if (user) {
                displayName = user.displayName;
            }
        }

        return displayName;
    }

    getUsersByPermission(permissionName: string, tenantId: string): Promise<IUserDto[]> {
        return this.endpointService.getUsersByPermissionName(permissionName, tenantId);
    }

    async getManagerHierarchy(userObjectId: string, processedIds: Set<string> = new Set()): Promise<string[]> {
        let managerHierArchy: string[] = [];
        const manager = await this.getUserByObjectId(userObjectId);

        if (manager?.managerObjectId != null && !processedIds.has(manager?.managerObjectId)) {
            processedIds.add(manager.managerObjectId);
            managerHierArchy.push(manager.managerObjectId);
            const nextManagerId = await this.getManagerHierarchy(manager.managerObjectId, processedIds);
            managerHierArchy.push(...nextManagerId);
        }

        return managerHierArchy;
    }

}
